import React, { useEffect, useReducer } from 'react';
import classNames from 'classnames';

import './checkbox-tree.scss';
import { sizeDisplay, PathUtils } from '../utils';
import { FileObject } from '../dff-analyse/dff-analyse';
import { useSelector } from 'react-redux';
import * as selectors from '../dff/dff-selectors';
import ReactTooltip from 'react-tooltip';

export interface CheckboxTreeProps {
  file?: FileObject;
  onCheckboxChange: Function;
  selectedPaths: string[];
  showOnlyDirs: boolean;
}

const initialState = {
  tree: {},
  isOpen: false,
}

function reducer(state, action) {
  switch (action.type) {
    case 'toggleFileOpen':
      return { ...initialState, isOpen: action.payload };
    default:
      return state;
  }
}

export const CheckboxTree: React.FC<CheckboxTreeProps> = ({ file, onCheckboxChange, selectedPaths, showOnlyDirs }) => {
  initialState.isOpen = file.isOpen;
  const [, dispatch] = useReducer(reducer, initialState);

  const isDir = (file: FileObject): boolean => {
    return file.file.type === 'dir';
  };

  const toggle = (e: React.MouseEvent<HTMLAnchorElement>, file: FileObject) => {
    e.preventDefault();
    file.isOpen = !file.isOpen;
    dispatch({ type: 'toggleFileOpen', payload: !file.isOpen });
  };

  const baseUrl = useSelector(selectors.baseUrl);

  const originalUrl = (file: FileObject): string => {
    const parentPath = PathUtils.parent(file.path);
    const name = PathUtils.baseName(file.path);

    return `${baseUrl}/app/storage/${file.mountId}?path=${encodeURIComponent(parentPath)}&name=${encodeURIComponent(name)}`;
  };

  const handleRightClick = (
    e: React.MouseEvent<any>,
    f: FileObject
  ): void => {
    e.preventDefault();
    const url = originalUrl(f);
    window.open(url, '_blank');    
  };

  useEffect(() => {
    ReactTooltip.rebuild();
  }, []);

  return (
    <table className="checkbox-tree">
      <tbody>
        {
          file.children.filter(f => showOnlyDirs ? (isDir(f) && f.showInTree) : true).map((f, index) => (
            <tr className="checkbox-tree-item clearfix" key={index}>
              <td>
                <table>
                  <tbody>
                    <tr>
                      <td className="col-name">
                        {
                          isDir(f) ? (
                            <div className='checkbox-tree-item-content'>
                              { f.isLocked ? (
                                <div className='checkbox-tree-item-content__locked' data-tip={f.tooltip}></div>
                              ) : (
                                <input
                                  className='checkbox-tree-item-content-checkbox'
                                  type="checkbox"
                                  checked={selectedPaths.includes(f.path) || false}
                                  disabled={f.descendants !== 0}
                                  onChange={e => onCheckboxChange(f.path)}
                                />
                              )}
                              <a href="#" onClick={(e) => toggle(e, f)} onContextMenu={e => handleRightClick(e, f)}>
                                <span className={classNames('checkbox-tree-item-type-dir', {'empty-folder': f.descendants === 0 && !f.isLocked})}>
                                  <div className={classNames('arrow', {'arrow--is-open': f.isOpen} )}></div>
                                  {f.file.name}
                                </span>
                              </a>
                            </div>
                          ) : (
                              <div>
                                <a href={originalUrl(f)} className="checkbox-tree-item-type-file" target="_blank">
                                  <div className="dot"></div>
                                  {f.file.name}
                                </a>
                              </div>
                            )
                        }
                      </td>
                    </tr>
                    <tr>
                      <td className="add-inline">
                        {
                          isDir(f) && f.isOpen ? (
                            <CheckboxTree file={f} onCheckboxChange={onCheckboxChange} selectedPaths={selectedPaths} showOnlyDirs={showOnlyDirs} />
                          ) : null
                        }
                      </td>
                    </tr>
                  </tbody>

                </table>
              </td>
            </tr>
          ))
        }
      </tbody>

    </table>
  );
};
